import { PASSWORD_MIN_LENGTH } from '@erp/shared';
import { useEffect, useState } from 'react';

import { api } from './api.ts';

interface CheckResult {
  ok: boolean;
  message?: string;
}

/**
 * Asks the API (local leaked list + HIBP k-anonymity, done server-side) whether a
 * candidate password is acceptable, a moment after the user stops typing. The
 * browser never contacts any third party.
 */
export function usePasswordCheck(
  path: string,
  body: Record<string, unknown>,
  password: string,
): string | null {
  // The verdict belongs to the password it was computed for (no stale messages).
  const [result, setResult] = useState<{ password: string; message: string | null } | null>(null);
  const bodyKey = JSON.stringify(body);
  const eligible = Array.from(password).length >= PASSWORD_MIN_LENGTH;
  useEffect(() => {
    if (!eligible) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api<CheckResult>(path, {
        method: 'POST',
        body: { ...(JSON.parse(bodyKey) as Record<string, unknown>), password },
        signal: controller.signal,
      })
        .then((check) => {
          setResult({ password, message: check.ok ? null : (check.message ?? null) });
        })
        .catch(() => {
          // A failed check never blocks: the API validates again on submit.
        });
    }, 600);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [path, bodyKey, password, eligible]);
  return eligible && result?.password === password ? result.message : null;
}
